import React from 'react';
import { CMXIcon } from '@codametrix/ui-components';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import {
  Popover,
  Portal,
  PopoverTrigger,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter
} from '@chakra-ui/react';
import { AutocompleteMultiSelect } from './auto-complete-multiselect';
import { DateFilter } from './date-filter';
import { objectUtils, cmxDateTime } from '@codametrix/ui-common';
const { isEqual, isEmpty, cloneDeep } = objectUtils;

export type CommonFilterProps = {
  filterData: CMxCommonApp.SharedFilter[];
  relatedCasesFilterSelection: CMxCommonApp.FilterSelection;
  onApply: (options: CMxCommonApp.FilterSelection) => void;
};

type CommonFilterState = {
  isOpen: boolean;
  currentFilter: string | null;
  searchQuery: string;
  filterSelection: CMxCommonApp.FilterSelection;
  modifiedFilterData: CMxCommonApp.SharedFilter[];
  defaultFilterSelection: CMxCommonApp.FilterSelection;
  presetTypeSelected?: CMxCommonApp.DatePresets;
};

class CommonFilter extends React.Component<
  CommonFilterProps,
  CommonFilterState
> {
  constructor(props: CommonFilterProps) {
    super(props);
    this.state = {
      isOpen: false,
      currentFilter: '',
      searchQuery: '',
      filterSelection: {},
      modifiedFilterData: [],
      defaultFilterSelection: {}
    };
    this.toggleFilter = this.toggleFilter.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
    this.handleExcludeAndInclude = this.handleExcludeAndInclude.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.filterBody = this.filterBody.bind(this);
    this.footerChildren = this.footerChildren.bind(this);
    this.handleCheckboxClick = this.handleCheckboxClick.bind(this);
    this.resetOptionsState = this.resetOptionsState.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
  }

  componentDidMount(): void {
    this.resetOptionsState();
  }

  resetOptionsState(clearFilter?: boolean) {
    const { filterData, relatedCasesFilterSelection } = this.props;
    let defaultFilterSelection = {};
    let filterSelection = {};
    let sortedData = [];
    const today = new Date();
    if (filterData?.length) {
      filterData?.forEach((filter) => {
        if (filter.viewType !== 'date_range_with_preset') {
          const defaults: { [key: string]: boolean } = {};
          filter?.filterOptions?.forEach((item) => {
            defaults[item?.key] = item?.defaultSelection === 'INCLUDE';
          });
          defaultFilterSelection[filter.type] = defaults;
        } else {
          const dateDefaults: { [key: string]: Date } = {};
          if (filter?.dateFilterOptions) {
            dateDefaults.startDate = filter?.dateFilterOptions?.startDate
              ? filter?.dateFilterOptions?.startDate
              : today;
            dateDefaults.endDate = filter?.dateFilterOptions?.endDate
              ? filter?.dateFilterOptions?.endDate
              : today;
            defaultFilterSelection[filter.type] = dateDefaults;
          }
        }
      });

      if (isEmpty(relatedCasesFilterSelection) || clearFilter) {
        filterSelection = defaultFilterSelection;
      } else {
        filterSelection = relatedCasesFilterSelection;
      }

      sortedData = [...filterData].sort(
        (a, b) => a?.displayOrder - b?.displayOrder
      );
    }

    this.setState({
      defaultFilterSelection,
      filterSelection: filterSelection,
      modifiedFilterData: sortedData,
      currentFilter: sortedData?.length ? sortedData[0].type : '',
      presetTypeSelected: {} as CMxCommonApp.DatePresets
    });
  }

  handleFilterChange(type) {
    this.setState({ currentFilter: type, searchQuery: '' });
  }

  handleSearchInputChange(searchQuery: string) {
    this.setState({ searchQuery });
  }

  handleReset() {
    this.resetOptionsState(true);
  }

  handleCancel() {
    this.toggleFilter();
  }

  handleApply() {
    this.props.onApply(this.state.filterSelection);
    this.toggleFilter();
  }

  handleExcludeAndInclude(isInclude: boolean) {
    const { currentFilter, filterSelection, modifiedFilterData } = this.state;

    const selection = isInclude;
    const defaults: { [key: string]: boolean } = {};
    const currentFilterData = modifiedFilterData.find(
      ({ type }) => type === currentFilter
    );
    if (currentFilterData) {
      currentFilterData?.filterOptions.forEach((item) => {
        defaults[item?.key] = selection;
      });
    }
    const newFilterSelection = {
      ...filterSelection,
      [currentFilter]: defaults
    };
    this.setState({ filterSelection: newFilterSelection });
  }

  handleCheckboxClick(optionKey) {
    const { currentFilter, filterSelection, modifiedFilterData } = this.state;
    const currentFilterData = modifiedFilterData.find(
      ({ type }) => type === currentFilter
    );
    const defaults: { [key: string]: boolean } = {};
    currentFilterData?.filterOptions.forEach((item) => {
      let checkboxValue = filterSelection[currentFilter][item?.key];
      if (item?.key === optionKey) {
        checkboxValue = checkboxValue ? !checkboxValue : true;
      }
      defaults[item?.key] = checkboxValue as boolean;
    });
    const newfilterSelection = {
      ...filterSelection,
      [currentFilter]: defaults
    };

    this.setState({ filterSelection: newfilterSelection });
  }

  toggleFilter() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  handleDateChange(
    startDate,
    endDate,
    presetTypeSelected: CMxCommonApp.DatePresets
  ) {
    const { filterSelection, currentFilter } = this.state;
    const newfilterSelection = {
      ...filterSelection,
      [currentFilter]: { ...filterSelection[currentFilter], startDate, endDate }
    };
    this.setState({ filterSelection: newfilterSelection, presetTypeSelected });
  }

  footerChildren() {
    return (
      <div className="actions">
        <button
          key="actions-reset"
          onClick={this.handleReset}
          className="chakra-secondary-button reset-btn">
          Reset
        </button>
        <button
          key="actions-cancel"
          onClick={this.handleCancel}
          className="chakra-secondary-button cancel-btn">
          Cancel
        </button>
        <button
          key="actions-apply"
          onClick={this.handleApply}
          className="chakra-primary-button submit-btn">
          Apply
        </button>
      </div>
    );
  }

  filterBody() {
    const {
      searchQuery,
      filterSelection,
      currentFilter,
      modifiedFilterData,
      defaultFilterSelection
    } = this.state;

    let currentFilterObj = modifiedFilterData?.find(
      ({ type }) => type === currentFilter
    );

    let filterBody;
    switch (currentFilterObj?.viewType) {
      case 'date_range_with_preset':
        filterBody = (
          <DateFilter
            currentFilter={currentFilter}
            filterData={currentFilterObj}
            handleDateChange={this.handleDateChange}
            filterSelection={filterSelection?.[currentFilter]}
            defaultFilterSelection={defaultFilterSelection}
          />
        );
        break;
      default:
        filterBody = (
          <AutocompleteMultiSelect
            currentFilter={currentFilter}
            filterSelection={filterSelection}
            filterData={modifiedFilterData}
            defaultFilterSelection={defaultFilterSelection}
            handleExcludeAndInclude={this.handleExcludeAndInclude}
            handleCheckboxClick={this.handleCheckboxClick}
            searchQuery={searchQuery}
            handleSearchInputChange={this.handleSearchInputChange}
          />
        );
    }
    return filterBody;
  }

  render() {
    const { relatedCasesFilterSelection } = this.props;
    const {
      currentFilter,
      modifiedFilterData,
      isOpen,
      defaultFilterSelection,
      presetTypeSelected,
      filterSelection
    } = this.state;
    let filterButtonDateContent;
    let currentFilterObj = modifiedFilterData?.find(
      ({ type }) => type === currentFilter
    );

    const isDefaultFilterStateChanged =
      !isEmpty(relatedCasesFilterSelection) &&
      !isEqual(defaultFilterSelection, relatedCasesFilterSelection);

    if (isEmpty(presetTypeSelected)) {
      const dateFilterCofig = filterSelection['date_range'];
      const starteDateToShow = dateFilterCofig?.startDate
        ? cmxDateTime.format(
            dateFilterCofig.startDate.toString(),
            cmxDateTime.FORMATS.DATE_OF_SERVICE
          )
        : '';
      const endDateToshow = dateFilterCofig?.endDate
        ? cmxDateTime.format(
            dateFilterCofig.endDate.toString(),
            cmxDateTime.FORMATS.DATE_OF_SERVICE
          )
        : '';
      filterButtonDateContent =
        starteDateToShow == endDateToshow
          ? starteDateToShow
          : `${starteDateToShow} - ${endDateToshow}`;
    } else {
      filterButtonDateContent = presetTypeSelected?.name || '';
    }

    return (
      <div className="common-filter">
        <Popover
          isOpen={isOpen}
          onOpen={this.toggleFilter}
          onClose={this.toggleFilter}>

              {/* @ts-ignore */}
          <PopoverTrigger>
            <button className="chakra-secondary-button cancel-btn">
              {filterButtonDateContent && (
                <span> {filterButtonDateContent} |</span>
              )}

              <BiomeIcon
                iconName={IconName.Filter}
                iconSize="16"
                titleText="Filter"
              />

              {!isDefaultFilterStateChanged ? 'Default Filter' : 'Custom Filter'}
            </button>
          </PopoverTrigger>
          <Portal>
            {!!modifiedFilterData.length && (
              <PopoverContent
                className={`common-filter-popOver-content ${
                  currentFilterObj.viewType === 'date_range_with_preset'
                    ? 'lg'
                    : 'md'
                }`}
                data-testid="common-filter-popOver-content">
                <PopoverArrow />
                <PopoverBody className="common-filter-popOver-body">
                  <div
                    className={`common-filter-body ${
                      currentFilterObj.viewType === 'date_range_with_preset'
                        ? 'lg'
                        : 'md'
                    }`}>
                    <div className="filter-selector">
                      {modifiedFilterData.map(({ type, label }) => (
                        <div
                          key={type}
                          className={`filter-selector-item ${
                            type === currentFilter ? 'selected' : 'unSelected'
                          }`}
                          onClick={(e) => this.handleFilterChange(type)}>
                          <span>{label}</span>
                          <span className="filter-selector-icon">
                            <CMXIcon
                              svgIconName="BiomeChevronRight"
                              svgIconSize="20"
                              svgIconClass="chevron-right"
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                    {this.filterBody()}
                  </div>
                </PopoverBody>
                <PopoverFooter className="common-filter-popOver-footer">
                  {this.footerChildren()}
                </PopoverFooter>
              </PopoverContent>
            )}
          </Portal>
        </Popover>
      </div>
    );
  }
}

export { CommonFilter };
